import { useContext, useState } from "react";
import styles from "../Volume.module.css";
import {
    Collapsible,
    Divider,
    FormLayout,
    Link,
    Box,
    Tabs,
    BlockStack,
    Icon,
    Button,
    Card,
    Label,
    TextField,
} from "@shopify/polaris";
import { ClipboardIcon } from "@shopify/polaris-icons";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { useFormContext, useWatch, useController } from "react-hook-form";
import AutoComplete from "../../../../components/AutoComplete";
import { countryArray } from "../../../../utilities/constants";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { currencyToSymbols, TCurrencyCode } from "../../../utilities";
import TagsField from "../../../../components/TagsField";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { ActiveDatesCard } from "@shopify/discount-app-components";
import AndSeparator from "../../../../components/Separators/AndSeparator";
import { BUNDLE_VISIBILITY_OPT, BundleTypes, BundleVisibilityOptions } from "../../constants";
import { getAppBlockDeepLink } from "../../CommonFields/WidgetPositionsSelector";

// RHF field wrappers
import {
    FormCheckbox,
    FormTextField,
    FormSelect,
    FormRadioButton,
    FormSettingRow,
    useVariantFieldDisabled,
} from "../../../../components/FormFields";

interface AppConfigContextType {
    shop: string;
    extensionId: string;
    currency: string;
    iana_timezone?: string;
    [key: string]: any;
}

const VolumeTargetingForm = () => {
    const { control, setValue, getValues } = useFormContext();
    const appConfigContext = useContext(AppConfigContext) as AppConfigContextType;

    const bundleEmbedId = useWatch({ control, name: "bundle_embed_id" });
    const start_date = useWatch({ control, name: "start_date" });
    const end_date = useWatch({ control, name: "end_date" });
    const applyDiscountOnComparePriceValue = useWatch({ control, name: "apply_discount_on_compare_price" });
    const targetingTypeValue = useWatch({ control, name: "targeting.targetingType" }) ?? "all";

    const isScheduleAbDisabled = useVariantFieldDisabled("start_date");
    const isTargetingAbDisabled = useVariantFieldDisabled("targeting");
    const isHideNativeElementsDisabled = useVariantFieldDisabled("hide_native_elements");

    const [expandPosition, setExpandPosition] = useState(true);
    const [expandPricingSection, setExpandPricingSection] = useState(false);
    const [expandSchedule, setExpandSchedule] = useState(false);
    const [expandCustomerTargeting, setExpandCustomerTargeting] = useState(false);
    const [expandBundleVisibility, setExpandBundleVisibility] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const { trackEmbedCodeCopy, trackFormSections, trackTargeting } = useBundleMixpanelHook();

    // Needed for fields whose values we interact with in callbacks
    const { field: hideNativeField } = useController({ name: "hide_native_elements", control, defaultValue: "" });
    const { field: customerTagsField } = useController({ name: "targeting.customerTags", control, defaultValue: [] });
    const { field: customerCountriesField } = useController({
        name: "targeting.customerCountries",
        control,
        defaultValue: [],
    });

    const handleCopyText = (text: string) => {
        navigator.clipboard.writeText(text);
        shopify.toast.show("Embed Code copied");
        trackEmbedCodeCopy({ cta: "Embed Code Copied", ctaType: "Copy", bundleType: BundleTypes.VOLUME_DISCOUNT });
    };

    const handleResetTargeting = () => {
        trackTargeting({
            cta: "Customer Targeting",
            targetType: "Display Bundle to",
            value: "All",
            bundleType: BundleTypes.VOLUME_DISCOUNT,
        });
        setValue(
            "targeting",
            {
                ...getValues("targeting"),
                includeCustomers: "all",
                excludeCustomers: "none",
                includeCustomerData: "",
                excludeCustomerData: "",
                shopifyMarketsTargeting: "all",
                includeLocations: [],
                excludeLocations: [],
                shopifyMarketsTargetingData: [],
                targetingType: "all",
            },
            { shouldDirty: true },
        );
    };

    const handleSpecificTargeting = () => {
        trackTargeting({
            cta: "Customer Targeting",
            targetType: "Display Bundle to",
            value: "Specific",
            bundleType: BundleTypes.VOLUME_DISCOUNT,
        });
        setValue("targeting.targetingType", "specific", { shouldDirty: true });
    };

    const embedValue = `<div data-kefi-bundle-id="${bundleEmbedId}"></div>`;

    const visibleTabs = [
        { id: "visibility", content: "Visibility", accessibilityLabel: "Visibility", panelID: "visibility-panel" },
        { id: "pricing", content: "Pricing", accessibilityLabel: "Pricing", panelID: "pricing-panel" },
    ];

    const currentTabId = visibleTabs[selectedTab]?.id;

    const sectionArrow = (open: boolean) => (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transform: open ? "rotate(90deg)" : "rotate(270deg)",
            }}
        >
            <MdOutlineArrowBackIosNew size={14} />
        </div>
    );

    return (
        <div className={styles.collapsibleFormBox}>
            {/* ── 1. Widget Position ───────────────────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandPosition((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.VOLUME_DISCOUNT,
                        });
                        return !ps;
                    })
                }
            >
                <div>Widget Position</div>
                {sectionArrow(expandPosition)}
            </div>
            <Collapsible open={expandPosition} id="widget-position-collapsible">
                <FormLayout>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, lineHeight: "20px" }}>
                        <div>
                            By default, the widget will appear <b>below Add-to-cart form</b>. <br /> To change its
                            position, open the&nbsp;
                            <Link
                                url={getAppBlockDeepLink({
                                    shop: appConfigContext.shop,
                                    extensionId: appConfigContext.extensionId,
                                    bundleType: BundleTypes.VOLUME_DISCOUNT,
                                })}
                                external
                            >
                                Shopify Theme Editor
                            </Link>
                            ,&nbsp;and drag the app block to your preferred location.
                        </div>
                        <div>
                            <Link
                                external
                                url="https://bundles-help.keficommerce.com/faq/what-s-the-best-way-to-position-bundles-on-my-product-pages"
                            >
                                Need help?
                            </Link>
                        </div>
                    </div>
                    <Divider />
                    {bundleEmbedId && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                            <TextField
                                label="Embed this bundle manually in any page via HTML"
                                autoComplete="off"
                                value={embedValue}
                                readOnly
                                connectedRight={
                                    <div style={{ marginLeft: "8px" }}>
                                        <Button
                                            onClick={() => handleCopyText(embedValue)}
                                            size="slim"
                                            icon={<Icon source={ClipboardIcon} />}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                }
                            />
                        </div>
                    )}
                    <div className={`${styles.formRow} ${styles.row2}`}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                            <Label id="hide_native_elements">Hide shopify elements</Label>
                            <TagsField
                                label="shopify elements"
                                bundleType={BundleTypes.VOLUME_DISCOUNT}
                                section="Display bar to"
                                labelHidden
                                placeholder="Add selectors"
                                value={hideNativeField.value}
                                onChange={hideNativeField.onChange}
                                autoComplete="off"
                                disabled={isHideNativeElementsDisabled}
                            />
                        </div>
                    </div>
                </FormLayout>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            {/* ── 2. Bundle Visibility ─────────────────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandBundleVisibility((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Bundle Visibility ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.VOLUME_DISCOUNT,
                        });
                        return !ps;
                    })
                }
            >
                <div>Bundle Visibility</div>
                {sectionArrow(expandBundleVisibility)}
            </div>
            <Collapsible open={expandBundleVisibility} id="bundle-visibility-collapsible">
                <FormLayout>
                    <div className={styles.customLabelWrapper}>
                        <Label id="bundle_visibility_label">Show bundle on:</Label>
                    </div>
                    <div className={styles.formCol}>
                        {BundleVisibilityOptions.map((option) => (
                            <FormRadioButton
                                key={option.value}
                                name="bundle_visibility"
                                value={option.value}
                                label={option.label}
                                tooltipContent={
                                    option.value === BUNDLE_VISIBILITY_OPT.NO_PRODUCT_PAGES
                                        ? "Free gifts, upsell products, and bundle-product discounts will only work when these products are added through our widget. Hiding the widget from product pages will prevent customers from accessing these offers. Standard volume discounts will continue to apply as expected."
                                        : undefined
                                }
                                onChangeHook={() => {
                                    trackTargeting({
                                        cta: "Bundle Visibility",
                                        targetType: "Show bundle on",
                                        value: option.label,
                                        bundleType: BundleTypes.VOLUME_DISCOUNT,
                                    });
                                }}
                            />
                        ))}
                    </div>
                </FormLayout>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            {/* ── 3. Product Settings ──────────────────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandPricingSection((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.VOLUME_DISCOUNT,
                        });
                        return !ps;
                    })
                }
            >
                <div>Product Settings</div>
                {sectionArrow(expandPricingSection)}
            </div>
            <Collapsible open={expandPricingSection} id="product-settings-collapsible">
                <Box>
                    <Tabs tabs={visibleTabs} selected={selectedTab} onSelect={setSelectedTab}>
                        <Box padding="400" paddingBlockStart="0">
                            <Card>
                                {currentTabId === "visibility" && (
                                    <BlockStack>
                                        <FormSettingRow
                                            name="show_free_gift_popper"
                                            label="Show popper animation for free gifts"
                                            hideDivider
                                        />
                                    </BlockStack>
                                )}

                                {currentTabId === "pricing" && (
                                    <BlockStack>
                                        <FormSettingRow
                                            name="use_compare_at_price"
                                            label="Display 'Compare at' price"
                                            description="Show the Compare-at price in the product card."
                                            disabled={applyDiscountOnComparePriceValue}
                                        />
                                        <FormSettingRow
                                            name="apply_discount_on_compare_price"
                                            label="Apply discount on the Compare-at Price"
                                            description="Calculate discount based on Compare-at Price instead of selling price."
                                            tooltipContent={
                                                <div>
                                                    <p>
                                                        Your discount will be calculated based on the product's
                                                        Compare-at Price instead of the actual selling price.
                                                    </p>
                                                    <p>
                                                        <b>Important Note</b>
                                                    </p>
                                                    Your discount must be{" "}
                                                    <b>greater than the discount Shopify already shows&nbsp;</b> between
                                                    the Compare-at Price and the Actual Price.
                                                    <p>
                                                        <b>Example:</b>
                                                    </p>
                                                    <ul>
                                                        <li>Compare-at Price: $100</li>
                                                        <li>Actual Price: $90</li>
                                                        <li>→ Shopify already shows a 10% discount.</li>
                                                    </ul>
                                                    <p>
                                                        To apply your custom discount correctly, you must set a discount{" "}
                                                        <b>&nbsp;greater than 10% in app</b>
                                                    </p>
                                                </div>
                                            }
                                            onChangeHook={(val) => {
                                                if (val) setValue("use_compare_at_price", true, { shouldDirty: true });
                                            }}
                                        />
                                        <FormSettingRow name="show_unit_price" label="Show unit prices" hideDivider />
                                    </BlockStack>
                                )}
                            </Card>
                        </Box>
                    </Tabs>
                </Box>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            {/* ── 4. Scheduling ───────────────────────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandSchedule((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.VOLUME_DISCOUNT,
                        });
                        return !ps;
                    })
                }
            >
                <div>Scheduling</div>
                {sectionArrow(expandSchedule)}
            </div>
            <Collapsible open={expandSchedule} id="scheduling-collapsible">
                <FormLayout>
                    <div style={{ marginBottom: "-1rem", marginTop: -3 }}>
                        {appConfigContext?.iana_timezone && (
                            <ActiveDatesCard
                                startDate={{
                                    value: start_date,
                                    onChange: (val: string) => setValue("start_date", val, { shouldDirty: true }),
                                }}
                                endDate={{
                                    value: end_date,
                                    onChange: (val: string | null) => setValue("end_date", val, { shouldDirty: true }),
                                }}
                                timezoneAbbreviation={appConfigContext.iana_timezone}
                                disabled={isScheduleAbDisabled}
                            />
                        )}
                    </div>
                </FormLayout>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            {/* ── 5. Customer Targeting ────────────────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandCustomerTargeting((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Customer Targeting ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.VOLUME_DISCOUNT,
                        });
                        return !ps;
                    })
                }
            >
                <div>Customer Targeting</div>
                {sectionArrow(expandCustomerTargeting)}
            </div>
            <Collapsible open={expandCustomerTargeting} id="customer-targeting-collapsible">
                <FormLayout>
                    <div className={styles.customLabelWrapper}>
                        <Label id="targeting_label">Display Bundle to:</Label>
                    </div>
                    <div className={styles.formCol}>
                        {/* All Customers radio — handled manually since it resets entire targeting object */}
                        <FormRadioButton
                            name="targeting.targetingType"
                            value="all"
                            label="All Customers"
                            onChangeHook={handleResetTargeting}
                        />
                        <FormRadioButton
                            name="targeting.targetingType"
                            value="specific"
                            label="Specific Customer Segment"
                            onChangeHook={handleSpecificTargeting}
                        />

                        {targetingTypeValue === "specific" && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "80%",
                                    marginLeft: 25,
                                }}
                            >
                                {/* Customer Tags */}
                                <div>
                                    <FormCheckbox
                                        name="targeting.customerTagsEnabled"
                                        label="Based on customer tags"
                                        onChangeSideEffect={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Display Bundle to",
                                                value: "Target customers based on customer tag",
                                                bundleType: BundleTypes.VOLUME_DISCOUNT,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <FormSelect
                                            name="targeting.customerTagType"
                                            label="Exclusion/Inclusion"
                                            labelHidden
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                        />
                                        <TagsField
                                            label="Customer tags"
                                            bundleType={BundleTypes.VOLUME_DISCOUNT}
                                            section="Display bar to"
                                            labelHidden
                                            placeholder="Add tags"
                                            value={customerTagsField.value}
                                            onChange={(val) =>
                                                setValue("targeting.customerTags", val, { shouldDirty: true })
                                            }
                                            autoComplete="off"
                                            disabled={isTargetingAbDisabled}
                                        />
                                    </div>
                                </div>

                                <AndSeparator
                                    styles={{ marginBottom: "16px", marginLeft: 26 }}
                                    textStyles={{ fontSize: 12 }}
                                />

                                {/* Customer Countries */}
                                <div>
                                    <FormCheckbox
                                        name="targeting.customerCountriesEnabled"
                                        label="Based on customer countries"
                                        onChangeSideEffect={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Display Bundle to",
                                                value: "Target customers based on country",
                                                bundleType: BundleTypes.VOLUME_DISCOUNT,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <FormSelect
                                            name="targeting.customerCountryType"
                                            label="Exclusion/Inclusion"
                                            labelHidden
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                        />
                                        <AutoComplete
                                            label="Based on customer countries"
                                            labelHidden
                                            optionsArray={countryArray.map(
                                                (country: { symbol: string; name: string }) => ({
                                                    value: country.symbol,
                                                    label: country.name,
                                                }),
                                            )}
                                            selected={customerCountriesField.value}
                                            onChange={(val) =>
                                                setValue("targeting.customerCountries", val, { shouldDirty: true })
                                            }
                                            placeholder="Countries"
                                            listLabel="Countries to include"
                                            disabled={isTargetingAbDisabled}
                                        />
                                    </div>
                                </div>

                                <AndSeparator
                                    styles={{ marginBottom: "16px", marginLeft: 26 }}
                                    textStyles={{ fontSize: 12 }}
                                />

                                {/* Minimum Lifetime Spent */}
                                <div>
                                    <FormCheckbox
                                        name="targeting.customerSpentEnabled"
                                        label="Based on minimum lifetime spent"
                                        onChangeSideEffect={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Hide Bundle from",
                                                value: "Minimum amount spent in store",
                                                bundleType: BundleTypes.VOLUME_DISCOUNT,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <FormSelect
                                            name="targeting.customerSpentType"
                                            label="Comparison"
                                            labelHidden
                                            options={[
                                                { label: "Greater than", value: "greater_than" },
                                                { label: "Less than", value: "less_than" },
                                            ]}
                                        />
                                        <FormTextField
                                            name="targeting.customerSpent"
                                            label="Spent amount"
                                            labelHidden
                                            placeholder="00"
                                            type="number"
                                            prefix={currencyToSymbols[appConfigContext.currency as TCurrencyCode]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </FormLayout>
            </Collapsible>
        </div>
    );
};

export default VolumeTargetingForm;
